import { getCombatantSaveModifier, createTargetedSaveRequest } from '../../utils/combatSavingThrows';
import { isProne } from '../../utils/conditions';
import { getEffectiveWalkSpeed } from '../../utils/movementCore.mjs';

import { cunningStrikeEffectsForLevel, cunningStrikeMaxEffects } from '../../../../shared/rules/cunningStrike.mjs';
import { hasSubclassFeature } from '../../../../shared/rules/subclasses/index.mjs';

export { CUNNING_STRIKE_FEATURE_ID, CUNNING_STRIKE_EFFECTS } from '../../../../shared/rules/cunningStrike.mjs';
export const POISONERS_KIT_KEY = 'poisoners-kit';

const itemKey = (value) => String(value || '').trim().toLowerCase()
  .replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const inventoryItemIdentity = (item) => {
  const canonical = item?.index ?? item?.key ?? item?.itemKey ?? item?.itemId ?? item?.slug;
  return itemKey(canonical ?? item?.displayName ?? item?.itemName ?? item?.name);
};

/** Shared inventory identity lookup; equipped and legacy inventory shapes remain supported. */
export const hasInventoryItem = (character, key) => {
  const wanted = itemKey(key);
  const entries = [
    ...(Array.isArray(character?.item) ? character.item : []),
    ...(Array.isArray(character?.inventory) ? character.inventory : []),
    ...Object.values(character?.equipment || {}),
  ];
  return entries.some((entry) => {
    const item = entry?.item || entry?.data || entry?.definition || entry;
    const quantity = Number(entry?.quantity ?? entry?.count ?? entry?.qty ?? item?.quantity ?? item?.count ?? item?.qty ?? 1);
    return inventoryItemIdentity(item) === wanted && Number.isFinite(quantity) && quantity > 0;
  });
};

export const getCunningStrikeMaxEffects = cunningStrikeMaxEffects;

export const calculateCunningStrikeDice = ({ baseDice, selectedEffects = [], maxEffects }) => {
  if (!Number.isInteger(baseDice) || baseDice < 0 || !Array.isArray(selectedEffects) ||
      !Number.isInteger(maxEffects) || maxEffects < 0 || selectedEffects.length > maxEffects) {
    throw new Error('Invalid Cunning Strike selection.');
  }
  const spentDice = selectedEffects.reduce((sum, effect) => {
    if (!Number.isInteger(effect?.dieCost) || effect.dieCost < 0) throw new Error('Cunning Strike die costs must be non-negative integers.');
    return sum + effect.dieCost;
  }, 0);
  if (spentDice > baseDice) throw new Error('Cunning Strike costs more dice than Sneak Attack provides.');
  return { baseDice, spentDice, remainingDice: baseDice - spentDice };
};

const normalizeSize = (target) => String(target?.size || target?.creatureSize || target?.details?.size || '').trim().toLowerCase();
export const isTripSizeEligible = (target) => !['huge', 'gargantuan'].includes(normalizeSize(target));

export const getCunningStrikeSaveDC = (character) => {
  const dexterity = Number(character?.abilityModifiers?.dexterity ?? character?.abilityModifiers?.dex ??
    character?.abilities?.dexterity?.modifier ?? character?.abilities?.dex?.modifier ??
    Math.floor((Number(character?.dexterity ?? character?.dex ?? 10) - 10) / 2));
  const proficiency = Number(character?.proficiencyBonus ?? character?.profBonus);
  if (!Number.isFinite(dexterity) || !Number.isFinite(proficiency)) throw new Error('Cunning Strike requires authoritative Dexterity and proficiency values.');
  return 8 + dexterity + proficiency;
};

// The Hide action's Invisible on the attacker when it attacked: still on it, or ended by this attack (kept for Stealth Attack).
const hiddenForAttack = (combatState, attackerId, resolutionId) => Boolean(attackerId) && ((combatState?.activeEffects || [])
  .some((effect) => effect?.sourceFeature === 'action:hide' && String(effect.targetCombatantId) === String(attackerId))
  || Object.values(combatState?.turnActionLedger?.requests || {}).some((request) => request?.resolutionId === resolutionId && request.endedHide));

export const getCunningStrikeOptions = ({ rogueLevel, character, target, combatState, targetId, baseDice, attackerId, resolutionId }) => {
  const maxEffects = getCunningStrikeMaxEffects(rogueLevel);
  if (!maxEffects) return [];
  const supremeSneak = hasSubclassFeature(character, 'rogue:thief:supreme-sneak');
  return cunningStrikeEffectsForLevel(rogueLevel, { supremeSneak }).map((effect) => {
    const arithmetic = calculateCunningStrikeDice({ baseDice, selectedEffects: [effect], maxEffects });
    let disabledReason = null;
    if (effect.id === 'poison' && !hasInventoryItem(character, POISONERS_KIT_KEY)) disabledReason = "Requires Poisoner's Kit.";
    if (effect.id === 'trip' && !isTripSizeEligible(target)) disabledReason = 'Target must be Large or smaller.';
    if (effect.id === 'trip' && isProne(combatState, targetId)) disabledReason = 'Target is already Prone.';
    if (effect.id === 'stealth-attack' && !hiddenForAttack(combatState, attackerId, resolutionId)) disabledReason = 'Only while Hidden (the Hide action).';
    return { ...effect, remainingDice: arithmetic.remainingDice, disabled: Boolean(disabledReason), disabledReason };
  });
};

export const createCunningStrikeSaveRequest = ({ effect, resolutionId, attackerId, targetId, dc, target }) =>
  createTargetedSaveRequest({
    requestId: `${resolutionId}:cunning-strike:${effect.id}:save`,
    replayIdentity: `${resolutionId}:cunning-strike:${effect.id}`,
    sourceCombatantId: attackerId,
    targetCombatantId: targetId,
    ability: effect.saveAbility,
    dc,
    targetSaveModifier: getCombatantSaveModifier(target, effect.saveAbility),
  });

export const createWithdrawGrant = ({ character, combatState, combatantId, parentResolutionId }) => {
  const effectiveSpeed = getEffectiveWalkSpeed(character?.movement ?? character?.speed ?? character?.walkSpeed, combatState, combatantId) ?? 0;
  return {
    source: 'rogue:cunning-strike:withdraw', kind: 'special-movement', maxDistance: Math.floor(effectiveSpeed / 2),
    noOpportunityAttacks: true, immediate: true, parentResolutionId, turnSequence: combatState?.turnSequence,
  };
};
